import { useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { ChevronDown, Check } from 'lucide-react';
import cn from 'classnames';

import nl from '@assets/images/nl.jpg';
import en from '@assets/images/en.jpg';
import de from '@assets/images/de.png';
import fr from '@assets/images/fr.png';

type LangOpt = { value: string; label: string; fullName: string; flag: string };

const LANGUAGES: LangOpt[] = [
  { value: 'en', label: 'EN', fullName: 'English', flag: en },
  { value: 'nl', label: 'NL', fullName: 'Nederlands', flag: nl },
  { value: 'fr', label: 'FR', fullName: 'Français', flag: fr },
  { value: 'de', label: 'DE', fullName: 'Deutsch', flag: de }
];

export default function LanguageSwitcher({
  fullWidth
}: {
  fullWidth?: boolean;
}) {
  const { i18n } = useTranslation();
  const { getColour } = useThemeContext();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Normalize language code to base (e.g., "en-US" -> "en")
  const normalizeLanguage = (lang: string) => lang.split('-')[0].toLowerCase();

  // Get current language, normalized
  const currentLang = useMemo(
    () => normalizeLanguage(i18n.language),
    [i18n.language]
  );

  // Sync from localStorage
  useEffect(() => {
    const selected = localStorage.getItem('i18nextLng');
    if (selected) {
      const normalized = normalizeLanguage(selected);
      if (normalized !== currentLang) {
        i18n.changeLanguage(normalized);
      }
    }
  }, [i18n, currentLang]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentLanguage = useMemo(
    () => LANGUAGES.find(lang => lang.value === currentLang) || LANGUAGES[0],
    [currentLang]
  );

  const handleLanguageChange = (lang: LangOpt) => {
    i18n.changeLanguage(lang.value).then(() => {
      try {
        localStorage.setItem('i18nextLng', lang.value);
      } catch {
        // noop
      }
      setIsOpen(false);
    });
  };

  return (
    <div
      className={cn('relative', fullWidth ? 'flex-1' : 'inline-block')}
      ref={dropdownRef}
    >
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center gap-1.5 px-2 py-1 rounded-xl transition-all duration-200 hover:scale-105',
          fullWidth && 'w-full justify-start'
        )}
        style={{
          backgroundColor: getColour('secondLayer'),
          border: `1px solid ${getColour('border')}`,
          color: getColour('normalText')
        }}
        aria-label="Select language"
      >
        <img
          src={currentLanguage.flag}
          alt={currentLanguage.fullName}
          className="w-3.5 h-3.5 rounded-sm object-cover"
        />
        <span className="text-[11px] font-medium">{currentLanguage.label}</span>
        <ChevronDown
          size={12}
          className={cn('transition-transform duration-200', {
            'rotate-180': isOpen
          })}
          style={{ color: getColour('icon') }}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="absolute right-0 bottom-full mb-2 w-48 rounded-2xl shadow-pop z-50 overflow-hidden"
          style={{
            backgroundColor: getColour('secondLayer'),
            border: `1px solid ${getColour('hairline')}`
          }}
        >
          {LANGUAGES.map(lang => {
            const isSelected = lang.value === currentLang;
            return (
              <button
                key={lang.value}
                onClick={() => handleLanguageChange(lang)}
                className={cn(
                  'w-full flex items-center gap-3 px-2 py-1.5 transition-all duration-150',
                  'hover:translate-x-1'
                )}
                style={{
                  backgroundColor: isSelected
                    ? getColour('thirdLayer')
                    : 'transparent',
                  color: getColour('normalText'),
                  borderBottom: `1px solid ${getColour('hairline')}`
                }}
              >
                <img
                  src={lang.flag}
                  alt={lang.fullName}
                  className="w-6 h-6 rounded-sm object-cover flex-shrink-0"
                />
                <div className="flex-1 text-left">
                  <div className="text-sm font-medium">{lang.fullName}</div>
                  <div
                    className="text-xs"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {lang.label}
                  </div>
                </div>
                {isSelected && (
                  <Check
                    size={18}
                    className="flex-shrink-0"
                    style={{ color: getColour('activeText') }}
                  />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
